import { FC, useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import isEqual from 'react-fast-compare';
// eslint-disable-next-line no-restricted-imports -- открытый в карточке черновик помечается по адресу страницы
import { useLocation } from 'react-router';
import { useUi } from '@srm/ui/provider';
import { useLocalStorageState } from '@srm/ui/hooks/useLocalStorageState';
import { DocumentActionsService } from '@services/actions/DocumentActionsService';
import { LocationTypes } from '@models/actions/LocationTypes';
import { IBaseAction } from '@models/actions/IBaseAction';
import { useAnalytics } from '@/analytics';
import { ConfigService } from '@/configuration/services/configService';
import { IDebugSettings } from '@models/IDebugSettings';
import { useSidebarHold } from '@srm/ui/shell/AppShell';
import type { IDocumentDraft } from '@models/documentDraft/IDocumentDraft';
import { draftHref } from '@pages/document/useDocumentDraft';
import CreateDocumentMenu from './CreateDocumentMenu';

export interface ICreateDocumentLauncherProps {
    /** Раздел адреса: действия создания зависят от него */
    searchType: string | null;
    /** Запустить выбранное действие создания */
    onAction: (action: IBaseAction) => void;
}

/** Кнопка «Создать документ» и её меню. Состояние меню живёт здесь, а не в `Folders`: открытие и загрузка действий
 *  не перерисовывают дерево разделов. */
const CreateDocumentLauncher: FC<ICreateDocumentLauncherProps> = ({ searchType, onAction }) => {
    const { analytics } = useAnalytics();
    const location = useLocation();
    const { navigate } = useUi();
    const config = ConfigService.get();
    const [debugSettings] = useLocalStorageState<IDebugSettings>('Debug-Settings', {
        notifyFormulaErrors: config.environment !== 'PROD',
        showActionKeys: config.environment !== 'PROD',
    });

    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [actions, setActions] = useState<IBaseAction[]>([]);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    useSidebarHold(isOpen);

    const loadActions = useCallback(() => {
        if (isLoading) return;
        setIsLoading(true);
        DocumentActionsService.fetchActionsByLocation(undefined, LocationTypes.PlanningMenu, searchType)
            // тот же набор при повторном открытии не перерисовывает меню посреди анимации
            .then((res) => setActions((prev) => (isEqual(prev, res.data) ? prev : res.data)))
            .finally(() => setIsLoading(false));
    }, [isLoading, searchType]);

    const onToggle = useCallback(() => {
        const next = !isOpen;
        setIsOpen(next);
        if (!next) return;

        loadActions();
        analytics.trackEvent({
            category: 'Document',
            action: 'Клик по кнопке Создать документ',
            name: 'sidebar-create-doc',
        });
    }, [analytics, isOpen, loadActions]);

    const onClose = useCallback(() => setIsOpen(false), []);

    const onOpenDraft = useCallback((draft: IDocumentDraft) => void navigate(draftHref(draft)), [navigate]);
    // Черновик, открытый сейчас в карточке, — по адресу страницы: из лаунчера его не удалить.
    const isDraftOpen = useCallback(
        (draft: IDocumentDraft) => draftHref(draft) === location.pathname,
        [location.pathname],
    );

    return (
        <>
            <div
                className={clsx('create-doc-dialog-anchor', isLoading && 'create-doc-dialog-anchor--loading')}
                ref={anchorRef}
            >
                {/* своя кнопка: плашка с вырезанным плюсом, которая в свёрнутом меню сжимается до круга */}
                <button
                    type="button"
                    title="Создать документ"
                    aria-label="Создать документ"
                    aria-expanded={isOpen}
                    className="doc-button"
                    data-testid="create-doc-button"
                    onClick={onToggle}
                >
                    <span className="doc-button__inner">
                        <span className="doc-button__glyph" aria-hidden="true" />
                        <span className="doc-button__text">Создать документ</span>
                    </span>
                </button>
            </div>

            <CreateDocumentMenu
                anchorRef={anchorRef}
                opened={isOpen}
                // признак загрузки нужен только скелетону пустого меню: с прежним набором окно не перерисовывается
                isLoading={isLoading && actions.length === 0}
                actions={actions}
                onClose={onClose}
                onAction={onAction}
                showActionKeys={config.environment !== 'PROD' && debugSettings?.showActionKeys}
                draftsEnabled={config.application.autoSaveDocument ?? false}
                onOpenDraft={onOpenDraft}
                isDraftOpen={isDraftOpen}
            />
        </>
    );
};

export default CreateDocumentLauncher;
